/**
 * Lead Data Transformation Utilities
 *
 * This file contains utility functions for transforming API responses
 * into component-friendly data formats. These functions ensure consistent
 * data structure across different components and views.
 */

import { transformLeadWithPriority } from "./priorityCalculator";
import { getLocalTime } from "./dateUtils";

/**
 * Transform single lead API response to component format for detail view
 * Used specifically for individual lead details (from getLead API)
 *
 * @param {Object} apiData - Raw API response data
 * @param {string} fallbackId - Fallback lead ID if not found in API data
 * @returns {Object} Transformed lead data for component use
 */
export const transformLeadDetailsApiData = (apiData, fallbackId = null) => {
  // The API response structure is nested with BasicInformation, ContactInformation, etc.
  // Based on the actual API response structure provided

  // Helper function to extract value from UnmappedFields
  const getUnmappedFieldValue = (fieldLabel) => {
    const field = apiData.UnmappedFields?.find((f) =>
      f.FieldLabel?.toLowerCase().includes(fieldLabel.toLowerCase()),
    );
    return field?.FieldValue || null;
  };

  const transformedData = {
    id: apiData.BasicInformation?.LeadId || fallbackId,
    displayId:
      apiData.BasicInformation?.DisplayLeadId ||
      apiData.BasicInformation?.LeadId ||
      fallbackId,

    name:
      apiData.PersonalDetails?.FullName ||
      `${apiData.PersonalDetails?.FirstName || ""} ${apiData.PersonalDetails?.LastName || ""}`.trim() ||
      `${getUnmappedFieldValue("First Name") || ""} ${apiData.PersonalDetails?.LastName || ""}`.trim() ||
      getUnmappedFieldValue("First Name") ||
      apiData.PersonalDetails?.LastName ||
      // Fallback: Use email prefix if no name is available
      (apiData.ContactInformation?.EmailAddress ||
        apiData.ContactInformation?.Email ||
        getUnmappedFieldValue("Email Address") ||
        getUnmappedFieldValue("Email")
        ? (
          apiData.ContactInformation?.EmailAddress ||
          apiData.ContactInformation?.Email ||
          getUnmappedFieldValue("Email Address") ||
          getUnmappedFieldValue("Email")
        ).split("@")[0]
        : "Unknown Lead"),
    firstName:
      apiData.PersonalDetails?.FirstName ||
      getUnmappedFieldValue("First Name") ||
      "N/A",
    lastName:
      apiData.PersonalDetails?.LastName ||
      getUnmappedFieldValue("Last Name") ||
      "N/A",
    email:
      apiData.ContactInformation?.EmailAddress ||
      apiData.ContactInformation?.Email ||
      getUnmappedFieldValue("Email Address") ||
      getUnmappedFieldValue("Email") ||
      "N/A",
    mobile:
      apiData.ContactInformation?.MobileNumber ||
      apiData.ContactInformation?.Mobile ||
      getUnmappedFieldValue("Phone Number") ||
      getUnmappedFieldValue("Mobile") ||
      getUnmappedFieldValue("Guardian's Mobile Number") ||
      "N/A",
    city:
      apiData.ContactInformation?.City ||
      getUnmappedFieldValue("City") ||
      "N/A",
    state:
      apiData.ContactInformation?.State ||
      getUnmappedFieldValue("State") ||
      "N/A",
    country:
      apiData.ContactInformation?.Country ||
      getUnmappedFieldValue("Country") ||
      "N/A",
    gender: apiData.PersonalDetails?.Gender || "N/A",
    dob: apiData.PersonalDetails?.DOB || "N/A",
    program: apiData.AcademicInformation?.Program || "N/A",
    course: apiData.AcademicInformation?.Course || "N/A",
    leadScore: apiData.BasicInformation?.LeadScore || 0,
    status: apiData.BasicInformation?.StatusId || 0,
    priority: apiData.BasicInformation?.Priority || 1,
    campaign: apiData.BasicInformation?.Campaign || "N/A",
    source: apiData.SourceDetails?.LeadSource || "N/A",
    LeadSourceName: apiData.SourceDetails?.LeadSource || "N/A",
    LeadSourceFormId: apiData.SourceDetails?.LeadSourceFormId || apiData.BasicInformation?.LeadSourceFormId,
    companyName: apiData.SourceDetails?.CompanyName || "N/A",
    SourceDetails: apiData.SourceDetails || null,
    LeadAcademicCounsellorName: apiData.SourceDetails?.LeadAcademicCounsellorName,
    LeadAcademicCounsellorId: apiData.SourceDetails?.LeadAcademicCounsellorId,
    accountsCounsellorName: apiData.SourceDetails?.LeadAccountantCounsellorName || apiData.SourceDetails?.accountsCounsellorName || apiData.SourceDetails?.AccountsCounsellorName,
    stage: apiData.BasicInformation?.StageName || "N/A",
    subStage: apiData.BasicInformation?.SubStageName || "N/A",
    IsDuplicate: apiData.BasicInformation?.IsDuplicate ?? false,
    ParentLeadId: apiData.BasicInformation?.ParentLeadId || null,
    LeadUserId: apiData.BasicInformation?.LeadUserId,
    HasDuplicate: apiData.BasicInformation?.HasDuplicate ?? false,
    EmailVerified: apiData.ContactInformation?.EmailVerified ?? false,
    PhoneVerified: apiData.ContactInformation?.PhoneVerified ?? false,
    assignedTo: apiData.SourceDetails?.AssignedTo || null,
    assignedOwner: apiData.SourceDetails?.AssignedToName || "N/A",
    formInterestedIn: "N/A",
    lastActive:
      apiData.BasicInformation?.LastLoggedIn ||
      apiData.BasicInformation?.LastActive ||
      "N/A",
    addedOn: apiData.BasicInformation?.CreatedAt || "N/A",
    tags: [],
    remarks: apiData.BasicInformation?.Remarks || "",
    nextFollowUp: "N/A",
    activities: apiData.LeadActivities || [], // Include activities from API response
    followUps: apiData.FollowUps || {}, // Include follow-ups from API response
    // AFTER (fixed)
    upcomingFollowUps: (apiData.FollowUps?.Upcoming || []).map(f => ({
      ...f,
      ScheduledTime: f.ScheduledTime || f.time || null,   // preserve PascalCase for component
      scheduledTime: getLocalTime(f.ScheduledDate || f.date, f.ScheduledTime || f.time), // localized copy
      Remarks: f.Remarks || f.remarks || f.Notes || f.notes || f.Remark || f.remark || f.description || f.comment || f.FollowUpNote || f.NoteDescription || f.FollowUpRemarks || "", // Normalize Remarks field
    })), // Include upcoming follow-ups with localized time string (lowercase) and normalized remarks
    unmappedFields: apiData.UnmappedFields || [], // Include dynamic/custom fields
    Priority: apiData.BasicInformation?.Priority || "N/A",
    InteractionLevelColorCode: apiData.BasicInformation?.InteractionLevelColorCode || "N/A",
    InteractionLevelDescription: apiData.BasicInformation?.InteractionLevelDescription || "N/A",
    InteractionLevelName: apiData.BasicInformation?.InteractionLevelName || "N/A",
    PriorityDescription: apiData.BasicInformation?.PriorityDescription || "N/A",
    LeadRawJsonData: apiData.LeadRawJsonData || [],
    LeadFieldCategories: apiData.LeadFieldCategories || [],
    IsInternationalLead: apiData.BasicInformation?.IsInternationalLead ?? false,
    IsStudent: apiData.BasicInformation?.IsStudent ?? apiData.BasicInformation?.isStudent ?? apiData.BasicInformation?.is_student ?? false,
    IsApplication: apiData.BasicInformation?.IsApplication ?? apiData.BasicInformation?.isApplication ?? false,
    BasicInformation: apiData.BasicInformation || null,
    offerLetter: apiData.OfferLetter || null,
  };


  return transformedData;
};

/**
 * Transform leads list API response to component format for list/table view
 * Used for leads listing (from getLeads API)
 *
 * @param {Object} leadApiData - Single lead data from API response array
 * @returns {Object} Transformed lead data for table/list components
 */
export const transformLeadListApiData = (leadApiData) => {
  // Use the priority calculator to transform the lead data
  return transformLeadWithPriority(leadApiData);
};

/**
 * Transform array of leads from API to component format
 *
 * @param {Array} leadsArray - Array of lead objects from API
 * @returns {Array} Array of transformed lead objects
 */
export const transformLeadsArray = (leadsArray) => {
  if (!Array.isArray(leadsArray)) {
    return [];
  }
  return leadsArray.map(transformLeadListApiData);
};

/**
 * Get default/fallback lead data structure for error states
 *
 * @param {string} leadId - Lead ID for fallback data
 * @returns {Object} Default lead data structure
 */
export const getDefaultLeadData = (leadId = null) => {
  return {
    id: leadId,
    displayId: leadId,
    name: "Unknown Lead",
    firstName: "N/A",
    lastName: "N/A",
    email: "N/A",
    mobile: "N/A",
    city: "N/A",
    state: "N/A",
    country: "N/A",
    gender: "N/A",
    dob: "N/A",
    program: "N/A",
    course: "N/A",
    leadScore: 0,
    status: 0,
    priority: 1,
    campaign: "N/A",
    source: "N/A",
    companyName: "N/A",
    stage: "Unverified",
    assignedOwner: "N/A",
    formInterestedIn: "N/A",
    lastActive: "N/A",
    addedOn: "N/A",
    tags: [],
    nextFollowUp: "N/A",
  };
};
